import { FC, ReactNode, useMemo } from 'react';

import { FeatureFlag, useFeatureFlags } from '@cloudquery/base-users';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';

import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router';
import { generateUrl, paths } from 'shared/base';

import { PlatformPermissions } from '@/constants/platformPermissions';
import { useAllowedPlatformFeatures } from '@/hooks/useAllowedPlatformFeatures';
import { useHasTeamsPermissions } from '@/hooks/useHasTeamsPermissions';
import { MainContainer } from '@/layouts/mainContainer';

interface Props {
  children: ReactNode;
}

export const SettingsLayout: FC<Props> = ({ children }) => {
  const { pathname } = useLocation();
  const { allowedEditingFeatureFlags, featureFlags } = useFeatureFlags();

  const { enabled: customColumnsEnabled } = featureFlags[FeatureFlag.ALLOWED_CUSTOM_COLUMNS];

  const { dataPermissionsAndRolesEnabled } = useAllowedPlatformFeatures();

  const isAdmin = useHasTeamsPermissions([PlatformPermissions.ADMIN_READ]);

  const sections = useMemo(
    () =>
      [
        {
          label: 'Account',
          tabs: [
            {
              label: 'Profile',
              path: paths.platform.settings.account.index,
            },
            {
              label: 'Multi-factor authentication',
              path: paths.platform.settings.account.mfa,
            },
          ],
        },
        isAdmin && {
          label: 'Organization',
          tabs: [
            { label: 'Platform settings', path: paths.platform.settings.organization.settings },
            { label: 'Users', path: paths.platform.settings.organization.users },
            ...(dataPermissionsAndRolesEnabled
              ? [
                  {
                    label: 'Data permissions',
                    path: paths.platform.settings.organization.dataPermissions.index,
                  },
                  {
                    label: 'Roles',
                    path: paths.platform.settings.organization.roles.index,
                  },
                ]
              : []),
            { label: 'Single sign-on', path: paths.platform.settings.organization.sso },
            { label: 'API keys', path: paths.platform.settings.organization.apiKeys },
            ...(customColumnsEnabled
              ? [
                  {
                    label: 'Custom columns',
                    path: paths.platform.settings.organization.customColumns,
                  },
                ]
              : []),
            { label: 'Platform updates', path: paths.platform.settings.organization.updates },
            ...(allowedEditingFeatureFlags
              ? [
                  {
                    label: 'Feature flags',
                    path: paths.platform.settings.organization.featureFlags,
                  },
                ]
              : []),
            { label: 'Audit log', path: paths.platform.settings.organization.auditLogs },
            {
              label: 'Notification destinations',
              path: paths.platform.settings.organization.notificationDestinations,
            },
          ],
        },
      ].filter(Boolean) as {
        label: string;
        tabs: { label: string; path: string; tooltip?: string }[];
      }[],
    [isAdmin, customColumnsEnabled, allowedEditingFeatureFlags, dataPermissionsAndRolesEnabled],
  );

  const getSelectedTab = (tabs: { label: string; path: string }[]) => {
    return tabs.find((tab) => pathname.startsWith(tab.path));
  };

  return (
    <MainContainer noPadding={true}>
      <Stack direction="row" overflow="hidden" sx={{ height: '100vh' }}>
        <Stack
          px={2}
          py={3}
          sx={({ palette }) => ({
            backgroundColor: palette.secondary.darkest,
            flexShrink: 0,
            height: '100%',
            overflowY: 'auto',
            position: 'sticky',
            top: 0,
          })}
          width={240}
        >
          {sections.map((section) => (
            <Stack key={section.label} gap={1} marginBottom={2}>
              <Stack gap={0.5}>
                <Typography variant="overline">{section.label}</Typography>
                <Tabs
                  orientation="vertical"
                  scrollButtons="auto"
                  sx={{
                    '& .Mui-selected, & .MuiTab-root:hover': {
                      backgroundColor: 'primary.selected',
                      borderRadius: 1,
                      color: 'text.primary',
                    },
                    '& .MuiTab-root': {
                      '& .MuiTab-wrapper': {
                        alignItems: 'flex-start',
                      },
                      alignItems: 'flex-start',
                      margin: `0 !important`,
                      padding: 1,
                      textAlign: 'left',
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none',
                    },
                    '& .MuiTabs-list': {
                      gap: 0.5,
                    },
                  }}
                  textColor="primary"
                  value={getSelectedTab(section.tabs)?.path || false}
                  variant="scrollable"
                >
                  {section.tabs.map((tab) => (
                    <Tab
                      key={`${section.label}-${tab.label}`}
                      href={generateUrl({
                        pathname: tab.path,
                      })}
                      label={
                        tab.tooltip ? (
                          <Tooltip
                            key={`${section.label}-${tab.label}`}
                            placement="right"
                            title={tab.tooltip}
                          >
                            <Stack alignItems="center" direction="row" gap={1}>
                              {tab.label}
                              <InfoOutlineIcon fontSize="small" />
                            </Stack>
                          </Tooltip>
                        ) : (
                          tab.label
                        )
                      }
                      value={generateUrl({
                        pathname: tab.path,
                      })}
                    />
                  ))}
                </Tabs>
              </Stack>
            </Stack>
          ))}
        </Stack>
        <Stack flexGrow={1} paddingX={4} sx={{ height: '100%', overflowY: 'auto', paddingY: 3 }}>
          {children}
        </Stack>
      </Stack>
    </MainContainer>
  );
};
